import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Card, Container, Step, StepButton, Stepper, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { Loader, SGSButton, SGSForm } from 'components';
import { useAppContext } from 'core/contexts';
import { TypeRole, TypeTitre, UserFormData, Utilisateur, formStepListByMode } from 'core/models';
import { addServerErrors } from 'core/utils/formValidation';
import { hasRole } from 'core/utils/roles';
import { makeUniqueAndSort } from 'core/utils/utils';
import { FormInfos } from 'features/utilisateurs/components/form/formInfos';
import { useUserInfosContext } from 'features/utilisateurs/contexts/UtilisateurInfosProvider';
import { delay } from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import accountCreated from '../../../assets/account_created.svg';
import checkGreen from '../../../assets/checkGreen.svg';
import { utilisateurService } from '../../../core/services';
import { useUtilisateurFromContext } from '../contexts/UtilisateurFormProvider';
import { useUserPerimetreContext } from '../contexts/UtilisateurPerimetreProvider';
import styles from './Utilisateurs.module.scss';
import { FormAlerte } from './form/formAlerte';
import { FormCodeGestionnaire } from './form/formCodeGestionnaire';
import { FormDroits } from './form/formDroits';
import { FormPerimetre } from './form/formPerimetre';
import { FormValidation } from './form/validationSchema';

type Props = {};

const UserPageUpdate: React.FC<Props> = () => {
    const { t } = useTranslation();
    const { user, setToken } = useAppContext();
    const { selectedUser, setSelectedUser, isFormSubmitted, setIsFormSubmitted, isFormSuccess, setUserConnected, setIsValidatedEmail, setIsValidatedLogin } = useUtilisateurFromContext();
    const { setHasPerimetreErreur, setPerimetre, setSante, setPrevoyance, setSelectedColleges, setSelectedSouscrips, setSouscrips, setColleges, setLoadingPerimetre } = useUserPerimetreContext();
    const { loading, setLoading, role, setRole, setSelectedResponsable, setTitre } = useUserInfosContext();
    const [activeStep, setActiveStep] = useState(0);
    const [completed, setCompleted] = useState<{
        [k: number]: boolean;
    }>({});

    const steps = formStepListByMode('profil');

    /** STEPPER */

    const totalSteps = () => {
        return steps.length;
    };

    const isLastStep = () => {
        return activeStep === totalSteps() - 1;
    };

    const completedSteps = () => {
        return Object.keys(completed).length;
    };

    const allStepsCompleted = () => {
        return completedSteps() === totalSteps();
    };

    const handleStep = (step: number) => async () => {
        const isValid = await validateStep(activeStep);
        if (!isValid) return;
        setActiveStep(step);
        scrollTop();
    };

    const validateStep = async (activeStep) => {
        let result = false;
        switch (activeStep) {
            case 0:
                result = await trigger(['titre', 'nom', 'prenom', 'telephone', 'email', 'fonction', 'role', 'responsable', 'accesWeb', 'login']);
                return result;
            case 1:
                result = await trigger(['perimetre']);
                return result;
            case 2:
                result = await trigger(['roles']);
                return result;
            case 3:
                result = await trigger(['codeGestionnaireMultiple']);
                return result;
            case 4:
                result = true; //await trigger(['alerte']);
                return result;
            default:
                throw Error('Step validation is missing');
        }
    };

    const handleBack = async () => {
        const isValid = await validateStep(activeStep);
        if (!isValid) return;

        setActiveStep((prevActiveStep) => prevActiveStep - 1);
        scrollTop();
    };

    const handleComplete = async () => {
        const newCompleted = completed;
        newCompleted[activeStep] = true;
        setCompleted(newCompleted);
        delay(handleNext, 20);
    };

    const handleNext = async () => {
        const isValid = await validateStep(activeStep);
        if (!isValid) return;
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        scrollTop();
    };

    const scrollTop = () => {
        window.scrollTo(0, 0);
    };

    const souscripOptionsAndSelection = (souscripteurs, risks, perimetre) => {
        let souscripOpts = [];
        const souscriptIds = souscripteurs ?? [];
        perimetre.forEach((item) => {
            // Constraints
            const isFiltered = risks?.length === 0 ? false : true;
            // Get data
            if (risks.includes(item.typ) || !isFiltered) souscripOpts.push({ value: item.codSouscrip, label: item.souscrip });
        });
        souscripOpts = makeUniqueAndSort(souscripOpts);
        const souscripOptsSelected = souscripOpts.filter((opt) => souscriptIds.includes(opt.value));

        return { souscriptIds, souscripOpts, souscripOptsSelected };
    };

    const collegesOptionsAndSelection = (colleges, risks, souscriptIds, perimetre) => {
        let collegeOpts = [];
        const collegeIds = colleges ?? [];
        perimetre.forEach((item) => {
            // Constraints
            const isFiltered = risks.length === 0 ? false : true;
            const result = [true];
            result.push(risks.includes(item.typ));
            result.push(souscriptIds.includes(item.codSouscrip));
            // Get data
            if (!result.includes(false) || !isFiltered) collegeOpts.push({ value: item.codcol, label: item.college });
        });
        collegeOpts = makeUniqueAndSort(collegeOpts);
        let collegeOptsSelected = collegeOpts.filter((opt) => collegeIds.includes(opt.value));

        return { collegeIds, collegeOpts, collegeOptsSelected };
    };

    const convertApiResponseToFormData = (r: Utilisateur, perimetre) => {
        // RISKS
        const risks = r.perimetre?.risques ?? [];
        const { souscriptIds, souscripOpts, souscripOptsSelected } = souscripOptionsAndSelection(r.perimetre?.souscripteurs, risks, perimetre);
        const { collegeOpts, collegeOptsSelected } = collegesOptionsAndSelection(r.perimetre?.colleges, risks, souscriptIds, perimetre);
        const sante = r.perimetre?.risques ? r.perimetre.risques.includes('S') : false;
        const prevoyance = r.perimetre?.risques ? r.perimetre.risques.includes('P') : false;

        const newSelectedUser: UserFormData = {
            id: r.id,
            nom: r.nom,
            prenom: r.prenom,
            titre: r.titre,
            telephone: r.telephone,
            email: r.email,
            fonction: r.fonction,
            role: r.role,
            responsable: r.responsable,
            accesWeb: r.accesWeb ? 'true' : 'false',
            login: r.login,
            codeGestionnaire: r.codeGestionnaire ?? '',
            codeGestionnaireMultiple: r.codeGestionnaireMultiple,
            perimetre: {
                risques: {
                    sante,
                    prevoyance,
                },
                souscripteurs: souscripOptsSelected,
                colleges: collegeOptsSelected,
                excludeVIP: r.perimetre.excludeVIP
            },
            roles: r.roles,
            alertBia: {
                id: r.alertBia?.id ?? null,
                actif: r.alertBia?.actif ?? false,
                frequence: r.alertBia?.frequence.toString() ?? null,
                lastNotification: r.alertBia?.lastNotification ?? null,
            },
        };

        setSouscrips(souscripOpts);
        setColleges(collegeOpts);
        setSelectedUser(newSelectedUser);
    };

    const userData = async () => {
        // Set loader
        setLoading(true);
        setLoadingPerimetre(true);

        await Promise.all([utilisateurService.getUserConnected().catch(() => {}), utilisateurService.getPerimetre().catch(() => setHasPerimetreErreur(true))])
            .then(([userConnected, perimeter]) => {
                setIsValidatedEmail(true);
                setIsValidatedLogin(true);
                setUserConnected(userConnected);
                setPerimetre(perimeter);
                convertApiResponseToFormData(userConnected, perimeter);
            })
            .finally(() => {
                setLoading(false);
                setActiveStep(0);
                setCompleted({});
                setLoadingPerimetre(false);
            });
    };

    useEffect(() => {
        userData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validationSchema = FormValidation(selectedUser, 'update');

    const methods = useForm<UserFormData>({
        resolver: yupResolver(validationSchema),
        defaultValues: selectedUser,
    });

    const {
        handleSubmit,
        reset,
        setValue,
        getValues,
        control,
        trigger,
        watch,
        formState: { errors, isDirty },
        setError,
    } = methods;

    const { fields: fieldsCodeGestionnaire, append, remove } = useFieldArray({ name: 'codeGestionnaireMultiple', control });

    const handleFormCancel = () => {
        setSelectedUser({ ...selectedUser });
    };

    const handleServerSideErrors = (error) => {
        addServerErrors(error.response.data.formErrors, setError);
    };

    const isEmailChanged = async (user) => {
        if (selectedUser.email !== user.email) {
            const response = await utilisateurService.refreshToken();
            setToken(response.token);
        }
    };

    const handleSendForm = async (formData: UserFormData) => {
        setIsFormSubmitted(true);

        // Convert perimeter data
        let risques = [];
        if (formData.perimetre.risques.sante) risques.push('S');
        if (formData.perimetre.risques.prevoyance) risques.push('P');

        const souscripIds = formData.perimetre.souscripteurs.map((d) => d.value);
        const collegeIds = formData.perimetre.colleges.map((d) => d.value);

        const payload: Utilisateur = {
            id: formData.id,
            nom: formData.nom,
            prenom: formData.prenom,
            titre: formData.titre,
            telephone: formData.telephone,
            email: formData.email,
            fonction: formData.fonction,
            role: formData.role as TypeRole,
            responsable: formData.responsable,
            accesWeb: formData.accesWeb === 'true',
            login: formData.login,
            codeGestionnaire: formData.codeGestionnaire,
            codeGestionnaireMultiple: formData.codeGestionnaireMultiple,
            perimetre: {
                risques: risques,
                souscripteurs: souscripIds,
                colleges: collegeIds,
                excludeVIP: formData.perimetre.excludeVIP
            },
            roles: formData.roles,
            alertBia: {
                id: formData.alertBia.id,
                actif: formData.alertBia.actif,
                frequence: formData.alertBia.frequence,
                lastNotification: formData.alertBia.lastNotification,
            },
        };
        
        // TODO :: change putUser for my profil

        await utilisateurService
            .putUser(payload)
            .then(async () => {
                const allStepCompleted = {};
                steps.forEach((_, i) => (allStepCompleted[i] = true));
                isEmailChanged(payload);
                setIsFormSubmitted(false);
                setSelectedUser({ ...formData });
                toast.success('Profil mis à jour !');
            })
            .catch(() => {
                handleServerSideErrors(errors);
            });

        setIsFormSubmitted(false);
    };

    useEffect(() => {
        reset({ ...selectedUser });
        setTitre(selectedUser.titre as TypeTitre);
        setRole(selectedUser.role as TypeRole);
        // Perimetre
        setSante(selectedUser.perimetre.risques.sante);
        setPrevoyance(selectedUser.perimetre.risques.prevoyance);
        setSelectedSouscrips(selectedUser.perimetre.souscripteurs);
        setSelectedColleges(selectedUser.perimetre.colleges);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedUser]);

    useEffect(() => {
        if (role === TypeRole.ADMIN) {
            setSelectedResponsable(null);
            setValue('responsable', null);
        }
    }, [role, setValue, setSelectedResponsable]);

    const MsgNoRight = () => <Alert severity="warning">Vous ne disposez pas des droits pour modifier ces informations</Alert>;

    return (
        <Container>
            <Card>
                {!loading && (
                    <>
                        <Box sx={{ width: '100%', height: '100%' }}>
                            {!allStepsCompleted() && (
                                <Stepper activeStep={activeStep} alternativeLabel nonLinear>
                                    {steps.map((label, index) => (
                                        <Step key={label} className={styles.step}>
                                            <StepButton color="inherit" onClick={handleStep(index)}>
                                                <Typography className={styles.SGSStep}>{label}</Typography>
                                            </StepButton>
                                        </Step>
                                    ))}
                                </Stepper>
                            )}
                            <div>
                                {allStepsCompleted() ? (
                                    <React.Fragment>
                                        {isFormSuccess ? (
                                            <>
                                                <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }} className="mb-5">
                                                    <Box sx={{ flex: '1 1 auto' }} />
                                                    <img src={accountCreated} height={'249'} width={'auto'} alt="" />
                                                    <Box sx={{ flex: '1 1 auto' }} />
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, mb: 5 }}>
                                                    <Box sx={{ flex: '1 1 auto' }} />
                                                    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                                                        <img src={checkGreen} height={'40'} width={'auto'} alt="" />
                                                        <Typography style={{ color: '#46BE81', fontSize: '1.5rem', paddingLeft: '20px' }}>Compte modifié avec succès</Typography>
                                                    </Box>
                                                    <Box sx={{ flex: '1 1 auto' }} />
                                                </Box>
                                                <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }} className="mb-5 mt-5">
                                                    <Box sx={{ flex: '1 1 auto' }} />
                                                    <Box onClick={() => userData()} style={{ cursor: 'pointer' }}>
                                                        Mon profil
                                                    </Box>
                                                    <Box sx={{ flex: '1 1 auto' }} />
                                                </Box>
                                            </>
                                        ) : (
                                            <>
                                                {/* TDODO :: ajouter message */}
                                                <Typography>Erreur !!!!</Typography>
                                            </>
                                        )}
                                    </React.Fragment>
                                ) : (
                                    <React.Fragment>
                                        {activeStep === 0 && (
                                            <FormProvider {...methods}>
                                                <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                    <FormInfos watch={watch} setValue={setValue} getValues={getValues} isCurrentUser={true} mode={'profil'} trigger={trigger} />
                                                </SGSForm.Card>
                                            </FormProvider>
                                        )}
                                        {activeStep === 1 && (
                                            <FormProvider {...methods}>
                                                <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                    {!hasRole(user, ['ROLE_ADMIN']) && (
                                                        <Box className="text-center mb-4">
                                                            <MsgNoRight />
                                                        </Box>
                                                    )}
                                                    <FormPerimetre setValue={setValue} errors={errors} trigger={trigger} readOnly={true}></FormPerimetre>
                                                </SGSForm.Card>
                                            </FormProvider>
                                        )}
                                        {activeStep === 2 && (
                                            <FormProvider {...methods}>
                                                <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                    {!hasRole(user, ['ROLE_ADMIN']) && (
                                                        <Box className="text-center mb-4">
                                                            <MsgNoRight />
                                                        </Box>
                                                    )}
                                                    <FormDroits errors={errors} trigger={trigger} setValue={setValue} readOnly={true} getValues={getValues}></FormDroits>
                                                </SGSForm.Card>
                                            </FormProvider>
                                        )}
                                        {activeStep === 3 && (
                                            <FormProvider {...methods}>
                                                <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                    {!hasRole(user, ['ROLE_ADMIN']) && (
                                                        <Box className="text-center mb-4">
                                                            <MsgNoRight />
                                                        </Box>
                                                    )}
                                                    <FormCodeGestionnaire
                                                        fields={fieldsCodeGestionnaire}
                                                        append={append}
                                                        remove={remove}
                                                        getValues={getValues}
                                                        watch={watch}
                                                        trigger={trigger}
                                                        errors={errors}
                                                        readOnly={hasRole(user, ['ROLE_ADMIN']) ? false : true}
                                                    />
                                                </SGSForm.Card>
                                            </FormProvider>
                                        )}
                                        {activeStep === 4 && (
                                            <FormProvider {...methods}>
                                                <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                    <FormAlerte lastNotification={selectedUser.alertBia.lastNotification} mode={'profil'} />
                                                </SGSForm.Card>
                                            </FormProvider>
                                        )}

                                        <FormProvider {...methods}>
                                            <SGSForm.Card onSubmit={handleSubmit(handleSendForm)} shadow={false}>
                                                <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }} className="mb-4">
                                                    <Box style={{ width: '250px' }}>
                                                        <SGSButton disabled={activeStep === 0} onClick={handleBack}>
                                                            Précédent
                                                        </SGSButton>
                                                    </Box>
                                                    <Box className="mr-3 text-right" style={{ width: '350px' }}>
                                                        <SGSButton type="button" onClick={() => handleFormCancel()} disabled={!isDirty ? true : false}>
                                                            {t('global.cancel')}
                                                        </SGSButton>
                                                    </Box>
                                                    <Box className="text-left " style={{ width: '350px' }}>
                                                        <SGSButton type="submit" loading={isFormSubmitted} disabled={!isDirty ? true : false}>
                                                            {t('global.save')}
                                                        </SGSButton>
                                                    </Box>
                                                    <Box className="text-right " style={{ width: '250px' }}>
                                                        <SGSButton type="button" onClick={handleComplete} disabled={isLastStep()}>
                                                            Suivant
                                                        </SGSButton>
                                                    </Box>
                                                </Box>
                                            </SGSForm.Card>
                                        </FormProvider>
                                    </React.Fragment>
                                )}
                            </div>
                        </Box>
                    </>
                )}
                {loading && (
                    <Box className={styles.loading}>
                        <Box className="mr-5">
                            <Loader loading={loading} />
                        </Box>
                        <Box className="m-5 pr-5">Veuillez patienter durant le chargement de votre profil...</Box>
                    </Box>
                )}
            </Card>
        </Container>
    );
};

export default UserPageUpdate;
